import React, { useState, useEffect } from 'react';
import { useGetSuperAdminDashboardDetailsQuery } from '../../store/slices/apiSlice';
import TestAddProjectImages from '../../components/test/TestAddProjectImages';
import {
  Building2,
  Users,
  TrendingUp,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Tag,
  Eye,
  Plus,
  Settings,
  BarChart3,
  Calendar,
  Clock,
  Star,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Target,
  Award,
  Shield,
  Zap,
  Globe,
  CreditCard,
  PieChart,
  LineChart,
  RefreshCw,
  Download,
  Filter,
  Search,
  Bell,
  UserPlus,
  FileText,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  ChevronRight,
  MoreHorizontal,
  TrendingDown,
  Circle,
  Square,
  Triangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Mock data for enhanced dashboard
const mockRecentActivities = [
  {
    id: 1,
    type: 'investment',
    user: 'John Doe',
    action: 'invested $50,000',
    project: 'Luxury Apartments NYC',
    time: '2 minutes ago',
    status: 'completed',
    amount: 50000
  },
  {
    id: 2,
    type: 'project',
    user: 'Admin',
    action: 'created new project',
    project: 'Tech Hub Dubai',
    time: '15 minutes ago',
    status: 'pending',
    amount: 0
  },
  {
    id: 3,
    type: 'user',
    user: 'Sarah Wilson',
    action: 'registered',
    project: 'New User',
    time: '1 hour ago',
    status: 'completed',
    amount: 0
  },
  {
    id: 4,
    type: 'withdrawal',
    user: 'Mike Johnson',
    action: 'withdrew $25,000',
    project: 'Profit Withdrawal',
    time: '2 hours ago',
    status: 'completed',
    amount: 25000
  },
  {
    id: 5,
    type: 'investment',
    user: 'Emma Davis',
    action: 'invested $75,000',
    project: 'Commercial Plaza London',
    time: '3 hours ago',
    status: 'completed',
    amount: 75000
  }
];

const mockTopProjects = [
  {
    id: 1,
    name: 'Luxury Apartments NYC',
    category: 'Residential',
    progress: 100,
    raised: 10000000,
    target: 10000000,
    investors: 234,
    status: 'active',
    trend: 'up'
  },
  {
    id: 2,
    name: 'Tech Hub Dubai',
    category: 'Commercial',
    progress: 67,
    raised: 6700000,
    target: 10000000,
    investors: 189,
    status: 'active',
    trend: 'up'
  },
  {
    id: 3,
    name: 'Retail Center Miami',
    category: 'Retail',
    progress: 92,
    raised: 9200000,
    target: 10000000,
    investors: 312,
    status: 'active',
    trend: 'up'
  },
  {
    id: 4,
    name: 'Industrial Park Berlin',
    category: 'Industrial',
    progress: 45,
    raised: 4500000,
    target: 10000000,
    investors: 156,
    status: 'active',
    trend: 'down'
  }
];

const mockPerformanceMetrics = {
  monthlyGrowth: 12.5,
  userRetention: 94.2,
  avgInvestment: 45000,
  platformUptime: 99.9,
  customerSatisfaction: 4.8,
  processingTime: 2.3
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTestComponent, setShowTestComponent] = useState(false);

  const { data: dashboardData, isLoading, refetch } = useGetSuperAdminDashboardDetailsQuery();

  // Enhanced stats with trends and icons
  const stats = [
    {
      name: 'Total Users',
      value: dashboardData?.data?.total_users?.toString() || '0',
      icon: Users,
      color: 'text-blue-700',
      bg: 'bg-blue-100/50',
      border: 'border-blue-200',
      trend: '+12.5%',
      trendDirection: 'up',
      description: 'Active registered users'
    },
    {
      name: 'Total Projects',
      value: dashboardData?.data?.total_projects?.toString() || '0',
      icon: Building2,
      color: 'text-green-700',
      bg: 'bg-green-100/50',
      border: 'border-green-200',
      trend: '+8.3%',
      trendDirection: 'up',
      description: 'Active investment projects'
    },
    {
      name: 'Total Investments',
      value: dashboardData?.data?.total_investments?.toString() || '0',
      icon: TrendingUp,
      color: 'text-purple-700',
      bg: 'bg-purple-100/50',
      border: 'border-purple-200',
      trend: '+15.7%',
      trendDirection: 'up',
      description: 'Completed investments'
    },
    {
      name: 'Total Amount Invested',
      value: dashboardData?.data?.total_amount_invested?.toString() || '0',
      icon: DollarSign,
      color: 'text-orange-700',
      bg: 'bg-orange-100/50',
      border: 'border-orange-200',
      trend: '+22.1%',
      trendDirection: 'up',
      description: 'Total platform investments'
    },
  ];

  const formatCurrency = (amount: number | string | null | undefined, currencySymbol: string = '₦') => {
    const value = parseFloat(String(amount || '0')) || 0;
    return `${currencySymbol}${new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value)}`;
  };

  const formatNumber = (num: number | string) => {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(Number(num));
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'investment': return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'project': return <Building2 className="w-4 h-4 text-blue-600" />;
      case 'user': return <UserPlus className="w-4 h-4 text-purple-600" />;
      case 'withdrawal': return <DollarSign className="w-4 h-4 text-orange-600" />;
      default: return <Activity className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
      {/* Compact Toolbar */}
      <div className="bg-white border border-gray-200/80 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 shadow-sm">
        <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-600">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200/50 rounded-xl text-green-700 font-semibold">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span>Platform: Online</span>
          </div>
          <div className="flex items-center gap-2 text-gray-500 font-medium">
            <Clock className="w-4 h-4 text-gray-400" />
            <span>Last updated: {new Date().toLocaleTimeString()}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-500 font-medium">
            <Shield className="w-4 h-4 text-gray-400" />
            <span>Security: Active</span>
          </div>
        </div>

        <div className="flex items-center justify-between sm:justify-end gap-6 sm:gap-8">
          <div className="flex items-center gap-4 sm:gap-6 border-r border-gray-150 pr-6">
            <div className="text-center sm:text-left">
              <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Uptime</div>
              <div className="text-lg font-bold text-gray-900">{mockPerformanceMetrics.platformUptime}%</div>
            </div>
            <div className="text-center sm:text-left">
              <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Rating</div>
              <div className="text-lg font-bold text-gray-900">{mockPerformanceMetrics.customerSatisfaction}★</div>
            </div>
          </div>

          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-md shadow-blue-200 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:scale-100"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="bg-white rounded-2xl shadow-lg p-3 sm:p-4 md:p-6 animate-pulse border border-gray-100">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gray-200 rounded-xl"></div>
                <div className="w-12 h-3 sm:w-14 sm:h-4 bg-gray-200 rounded"></div>
              </div>
              <div className="h-6 sm:h-7 md:h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 sm:h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))
        ) : (
          stats.map((stat, index) => (
            <div
              key={stat.name}
              className="group bg-white rounded-2xl shadow-lg p-3 sm:p-4 md:p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className={`${stat.bg} p-2 sm:p-3 rounded-xl border ${stat.border} group-hover:scale-110 transition-transform duration-300`}>
                  <stat.icon className={`h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 ${stat.color}`} />
                </div>
                <div className="flex items-center gap-1 text-xs sm:text-sm">
                  {stat.trendDirection === 'up' ? (
                    <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />
                  )}
                  <span className={`font-medium ${stat.trendDirection === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                    {stat.trend}
                  </span>
                </div>
              </div>

              <div className="mb-2">
                <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1">
                  {stat.name === 'Total Amount Invested' ? formatCurrency(stat.value, '₦') : formatNumber(stat.value)}
                </p>
                <p className="text-xs sm:text-sm font-medium text-gray-600">{stat.name}</p>
              </div>

              <p className="text-xs text-gray-500">{stat.description}</p>
            </div>
          ))
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-8">
        {/* Recent Activities */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">Recent Activities</h2>
                  <p className="text-xs sm:text-sm text-gray-600">Latest platform activities and transactions</p>
                </div>
              </div>
              <button className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1">
                View All
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="space-y-3 sm:space-y-4">
              {mockRecentActivities.map((activity, index) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors duration-200"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    {getActivityIcon(activity.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-gray-900 truncate">
                        {activity.user}
                      </p>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                        {activity.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">
                      {activity.action} in <span className="font-medium">{activity.project}</span>
                    </p>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500">{activity.time}</p>
                      {activity.amount > 0 && (
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(activity.amount, '₦')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                <Zap className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Quick Actions</h2>
                <p className="text-sm text-gray-600">Manage your platform</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <button
              onClick={() => navigate('/admin/users')}
              className="w-full flex items-center gap-4 p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-all duration-300 group"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">User Management</h3>
                <p className="text-sm text-gray-600">Manage users and permissions</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform duration-300" />
            </button>

            <button
              onClick={() => navigate('/admin/projects')}
              className="w-full flex items-center gap-4 p-4 bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl transition-all duration-300 group"
            >
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Building2 className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Project Management</h3>
                <p className="text-sm text-gray-600">Create and manage projects</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform duration-300" />
            </button>

            <button
              onClick={() => navigate('/admin/investments')}
              className="w-full flex items-center gap-4 p-4 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-xl transition-all duration-300 group"
            >
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Investment Tracking</h3>
                <p className="text-sm text-gray-600">Monitor all investments</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform duration-300" />
            </button>

            <button
              onClick={() => navigate('/admin/bank-accounts')}
              className="w-full flex items-center gap-4 p-4 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-xl transition-all duration-300 group"
            >
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <CreditCard className="w-5 h-5 text-orange-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Bank Management</h3>
                <p className="text-sm text-gray-600">Manage bank accounts</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform duration-300" />
            </button>

            <button
              onClick={() => navigate('/admin/cta-management')}
              className="w-full flex items-center gap-4 p-4 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-xl transition-all duration-300 group"
            >
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Tag className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">CTA Management</h3>
                <p className="text-sm text-gray-600">Manage call-to-actions</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform duration-300" />
            </button>

            <button
              onClick={() => navigate('/admin/payment-methods')}
              className="w-full flex items-center gap-4 p-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-all duration-300 group"
            >
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <CreditCard className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Payment Methods</h3>
                <p className="text-sm text-gray-600">Manage payment gateways</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform duration-300" />
            </button>

            <button
              onClick={() => navigate('/admin/child-sponsorship')}
              className="w-full flex items-center gap-4 p-4 bg-pink-50 hover:bg-pink-100 border border-pink-200 rounded-xl transition-all duration-300 group"
            >
              <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Users className="w-5 h-5 text-pink-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Child Sponsorship</h3>
                <p className="text-sm text-gray-600">Manage child sponsorships</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Performance Metrics & Top Projects */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-8">
        {/* Performance Metrics */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Performance Metrics</h2>
                <p className="text-xs sm:text-sm text-gray-600">Platform performance indicators</p>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
              <div className="text-center p-3 sm:p-4 bg-white border border-blue-100 rounded-xl hover:bg-blue-50/30 transition-colors">
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-blue-900 mb-1">
                  {mockPerformanceMetrics.monthlyGrowth}%
                </div>
                <div className="text-xs sm:text-sm font-medium text-blue-700">Monthly Growth</div>
              </div>

              <div className="text-center p-3 sm:p-4 bg-white border border-green-100 rounded-xl hover:bg-green-50/30 transition-colors">
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-green-900 mb-1">
                  {mockPerformanceMetrics.userRetention}%
                </div>
                <div className="text-xs sm:text-sm font-medium text-green-700">User Retention</div>
              </div>

              <div className="text-center p-3 sm:p-4 bg-white border border-purple-100 rounded-xl hover:bg-purple-50/30 transition-colors">
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-purple-900 mb-1">
                  {formatCurrency(mockPerformanceMetrics.avgInvestment, '₦')}
                </div>
                <div className="text-xs sm:text-sm font-medium text-purple-700">Avg Investment</div>
              </div>

              <div className="text-center p-3 sm:p-4 bg-white border border-orange-100 rounded-xl hover:bg-orange-50/30 transition-colors">
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-orange-900 mb-1">
                  {mockPerformanceMetrics.processingTime}s
                </div>
                <div className="text-xs sm:text-sm font-medium text-orange-700">Processing Time</div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Projects */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-50 rounded-xl flex items-center justify-center">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">Top Projects</h2>
                  <p className="text-xs sm:text-sm text-gray-600">Best performing projects</p>
                </div>
              </div>
              <button className="text-green-600 hover:text-green-700 text-xs sm:text-sm font-medium flex items-center gap-1">
                View All
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="space-y-3 sm:space-y-4">
              {mockTopProjects.map((project, index) => (
                <div key={project.id} className="p-3 sm:p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{project.name}</h3>
                      <p className="text-sm text-gray-600">{project.category}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {project.trend === 'up' ? (
                        <ArrowUpRight className="w-4 h-4 text-green-600" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-red-600" />
                      )}
                      <span className={`text-sm font-medium ${project.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        {project.progress}%
                      </span>
                    </div>
                  </div>

                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3 overflow-hidden">
                    <div
                      className={`h-2 rounded-full transition-all duration-1000 ${project.progress >= 100 ? 'bg-gradient-to-r from-green-500 to-green-600 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : 'bg-gradient-to-r from-blue-500 to-blue-600'}`}
                      style={{ width: `${Math.min(project.progress, 100)}%` }}
                    ></div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600">
                        {formatCurrency(project.raised, '₦')} / {formatCurrency(project.target, '₦')}
                      </span>
                      <span className="text-gray-500">
                        {project.investors} investors
                      </span>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${project.progress >= 100 ? 'bg-green-600 text-white shadow-sm' : 'bg-blue-100 text-blue-800'}`}>
                      {project.progress >= 100 ? (
                        <>
                          <Award className="w-3 h-3 mr-1" />
                          Funded
                        </>
                      ) : (
                        project.status
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Platform Information</h2>
              <p className="text-sm text-gray-600">Contact details and support</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Email Support</h3>
                <p className="text-sm text-gray-600">info@diasporawise.com</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl border border-purple-200">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Office Location</h3>
                <p className="text-sm text-gray-600">123 Investment St, Finance City</p>
              </div>
            </div>
          </div>
        </div>

        {/* Test Component Section */}
        <div className="mt-8">
          <button
            onClick={() => setShowTestComponent(!showTestComponent)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Settings className="w-4 h-4" />
            {showTestComponent ? 'Hide' : 'Show'} API Test Component
          </button>

          {showTestComponent && (
            <div className="mt-4">
              <TestAddProjectImages />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;